import { useCallback } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useUser } from '@/services/UserService'
import { useSafeAnalytics } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Maybe } from '@/utils/types'

const subscribeToEmailGroupMutation = gql`
  mutation SubscribeToEmailGroup($input: SubscribeToEmailGroupInput!) {
    subscribeToEmailGroup(input: $input) {
      success
    }
  }
`

export function useCaptureOrderEmail() {
  const analytics = useSafeAnalytics()
  const { trackEmailCaptured } = useEcommerceEvents()
  const { query } = useSafeRouter()

  return useCallback(
    async function captureEmailAndSubscribe({
      subscriptionGroupId,
      email,
      position,
      projectSlug,
      ...customTrackPayload
    }: {
      subscriptionGroupId: string
      email: string
      position: string
      projectSlug: string
    } & Record<string, unknown>) {
      if (!email) {
        logger().warn('Email Capture: email is required', { projectSlug, position })
      }

      try {
        const user = analytics?.user?.()
        const userOrderEmail = user?.traits?.orderEmail
        if (email !== userOrderEmail) {
          await analytics.identify(user?.userId || null, {
            orderEmail: email,
          })
        }
      } catch (e) {
        logger().error('Email Capture: An error occurred while attempeting to identify a user for analytics', e)
      }

      await trackEmailCaptured({
        slug: query.slug,
        position,
        subscriptionGroupId,
        email,
        projectSlug,
        ...customTrackPayload,
      })
    },
    [query.slug, analytics, trackEmailCaptured],
  )
}

export function useCaptureEmailAndSubscribe(disableSubscribe?: boolean) {
  const { userId } = useUser()
  const { trackEmailCaptured } = useEcommerceEvents()
  const { query } = useSafeRouter()
  const [subscribeToEmailGroup] = useMutation(subscribeToEmailGroupMutation, {
    errorPolicy: 'all',
  })

  return useCallback(
    async function captureEmailAndSubscribe({
      subscriptionGroupId,
      email,
      position,
      projectSlug,
      ...customTrackPayload
    }: {
      subscriptionGroupId?: string
      email: string
      position: string
      projectSlug: Maybe<string>
    } & Record<string, unknown>) {
      if (!email) {
        logger().warn('Email Capture: subscriptionGroupId is required', { projectSlug, position })
      }
      if (!subscriptionGroupId && !disableSubscribe) {
        logger().warn('Email Capture: subscriptionGroupId is required', { projectSlug, position })
      }

      await trackEmailCaptured({
        slug: query.slug,
        position,
        subscriptionGroupId,
        email,
        projectSlug,
        ...customTrackPayload,
      })

      return !disableSubscribe
        ? await subscribeToEmailGroup({
            variables: {
              input: {
                userId: userId ?? null,
                email,
                subscriptionGroupId,
                projectSlug,
              },
            },
          })
        : Promise.resolve()
    },
    [trackEmailCaptured, query.slug, subscribeToEmailGroup, userId, disableSubscribe],
  )
}
